import { useState } from "react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

const empty = { address: "", access_code: "", wifi_name: "", wifi_password: "", parking: "", checkin_time: "16:00", checkout_time: "10:00", notes: "" };

export function PracticalInfoDialog({ listing, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...empty, ...(listing.practical || {}) });
  const [busy, setBusy] = useState(false);
  const f = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/listings/${listing.listing_id}/practical`, form);
      toast.success("Infos pratiques enregistrées — reprises dans le rappel veille");
      setOpen(false); onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const filled = listing.practical && Object.values(listing.practical).some((v) => v && v !== "16:00" && v !== "10:00");

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className={`inline-flex items-center gap-1 text-xs mt-1 hover:underline ${filled ? "text-olive" : "text-rose-brand"}`} data-testid={`practical-button-${listing.listing_id}`}>
          <KeyRound className="w-3.5 h-3.5" /> {filled ? "Infos pratiques ✓" : "Ajouter les infos pratiques"}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-olive">Infos pratiques · {listing.title}</DialogTitle>
          <DialogDescription>Envoyées automatiquement au voyageur la veille de son arrivée.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Adresse précise</Label><Input {...f("address")} placeholder="12 chemin des Oliviers, 84000 Avignon" data-testid="practical-address" /></div>
          <div><Label>Code d'accès / boîte à clés</Label><Input {...f("access_code")} placeholder="Boîte à clés à gauche du portail, code 1234" data-testid="practical-code" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nom du wifi</Label><Input {...f("wifi_name")} data-testid="practical-wifi" /></div>
            <div><Label>Mot de passe wifi</Label><Input {...f("wifi_password")} /></div>
            <div><Label>Arrivée à partir de</Label><Input type="time" {...f("checkin_time")} /></div>
            <div><Label>Départ avant</Label><Input type="time" {...f("checkout_time")} /></div>
          </div>
          <div><Label>Parking</Label><Input {...f("parking")} placeholder="2 places devant la maison" /></div>
          <div><Label>Consignes libres</Label><Textarea {...f("notes")} rows={4} placeholder="Poubelles le mardi, chauffage au sous-sol, numéro d'urgence…" data-testid="practical-notes" /></div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy} className="bg-olive text-white" data-testid="practical-save">{busy ? "..." : "Enregistrer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
